import _findIndex from 'lodash/findIndex';
import KEY_CODES from './KeyCodes';

const CONSTANTS = {
  LIST_ITEM_TABABLE_ITEM: 'ccfk-listitem-tabable',
  LIST_WRAPPER: 'ccfk-main-list-wrapper',
  LIST_ITEM_SELECTED: 'ccfk-listitem-selected',
  LIST_ITEM_CASCADING: 'ccfk-listitem-cascading',
  LIST_SUBMENU: 'ccfk-listitem-submenu',
  NEXT_MENU: 'ccfk-next-menu',
};

const {
  LIST_ITEM_TABABLE_ITEM,
  LIST_WRAPPER,
  LIST_ITEM_SELECTED,
  LIST_ITEM_CASCADING,
  LIST_SUBMENU,
} = CONSTANTS;

const {
  ARROW_UP,
  ARROW_DOWN,
  ARROW_LEFT,
  ARROW_RIGHT,
  TAB,
  HOME,
  END,
} = KEY_CODES;

const keyboardEnterKeys = [ARROW_UP, ARROW_DOWN, ARROW_RIGHT, ARROW_LEFT];
const isCascadingMenuItem = element => element && element.classList.contains(LIST_ITEM_CASCADING) && !element.disabled;
const isSubMenuItem = element => element && element.closest(`.${LIST_SUBMENU}`) !== null;

const isCascadingMenuItemOpen = (currentElement) => {
  const isCascadingItem = isCascadingMenuItem(currentElement);
  if (!isCascadingItem) {
    return false;
  }
  const thisParentId = currentElement.dataset.nextmenu;
  const childListStyle = document.querySelectorAll(`.${thisParentId}`)[0];
  return (childListStyle !== undefined);
};

// find the submenu for this cascading menu item and focus it (which will put focus on 1st menu item)
const shiftFocusToNextMenu = (currentElement) => {
  const thisParentId = currentElement.dataset.nextmenu;
  const childListStyle = document.querySelectorAll(`.${thisParentId}`)[0];
  const listWrapper = childListStyle && childListStyle.querySelectorAll(`.${LIST_WRAPPER}`)[0];
  listWrapper && (listWrapper as HTMLElement).focus();
};

// find the submenu for this cascading menu item and focus on last menu item
const shiftFocusToNextMenuLastItem = (currentElement) => {
  const thisParentId = currentElement.dataset.nextmenu;
  const childListStyle = document.querySelectorAll(`.${thisParentId}`)[0];
  const listWrapper = childListStyle && childListStyle.querySelectorAll(`.${LIST_WRAPPER}`)[0];
  const elements = listWrapper && listWrapper.querySelectorAll(`.${LIST_WRAPPER} li`);
  const last = elements && elements[elements.length - 1];
  last && (last as HTMLElement).focus();
};

// find parent menu listitem and focus it
const shiftFocusToPreviousMenu = (currentElement) => {
  const listParentAncestor = currentElement.closest(`.${LIST_SUBMENU}`);
  const thisParentListItem = listParentAncestor.dataset.prevmenu;
  const parentListItem = thisParentListItem && document.querySelectorAll(`.${thisParentListItem}`)[0];
  parentListItem && (parentListItem as HTMLElement).focus();
};

/** keyboard navigation on Lists or whatever elements use LIST_ITEM_TABABLE_ITEM and LIST_WRAPPER to identify keyboard items. */
const handleKeyDown = (props, ref, scrollIntoViewOnFocus = true, tabPressed = undefined, uniqueListIdentifier = undefined) => (event) => {
  if (!(ref && ref.current) || !props) {
    return;
  }
  const { keyboardNavigation, onKeyDown } = props;
  onKeyDown && onKeyDown(event);
  if (!keyboardNavigation) {
    return;
  }
  const { key } = event;
  if (key === TAB) {
    // caller needs to know when tab was pressed to set tabIndex of the wrapper.
    tabPressed && tabPressed(true);
    return;
  }
  tabPressed && tabPressed(false);
  const tabableClassName = uniqueListIdentifier ? `${LIST_ITEM_TABABLE_ITEM}-${uniqueListIdentifier}` : `${LIST_ITEM_TABABLE_ITEM}`;
  const elements = ref.current.querySelectorAll(`.${tabableClassName}`);
  const inCurrentList = event.target && event.target.classList && event.target.classList.contains(tabableClassName);
  if (!inCurrentList) {
    return;
  }
  const first = elements[0];
  const last = elements[elements.length - 1];
  const current = document.activeElement;
  const shouldNotFocus = element => !element.classList.contains(tabableClassName) || element.disabled;
  // focus on 1st item if the current is the list wrapper
  const focusedOnListWrapper = current.classList.contains(LIST_WRAPPER);
  if (keyboardEnterKeys.includes(key) && focusedOnListWrapper) {
    event.preventDefault();
    first && first.focus();
    return;
  }
  // focus on the actual list item component
  const currentIndex = _findIndex(elements, elem => (elem as HTMLElement).contains(current));
  const numberOfElements = elements.length;
  const prevItem = itemIndex => itemIndex > 0 && elements[itemIndex - 1];
  const nextItem = itemIndex => itemIndex < numberOfElements && elements[itemIndex + 1];
  const shiftFocus = (currentItemIndex, traverse:(any) => false | HTMLElement = () => false) => {
    let loopedOnce = false;
    let nextFocus = traverse(currentItemIndex);
    while (nextFocus) {
      if (nextFocus === first) {
        if (loopedOnce) return false;
        loopedOnce = true;
      }
      if (shouldNotFocus(nextFocus)) {
        nextFocus = traverse(nextFocus);
      } else {
        nextFocus.focus();
        // keep last element aligned to bottom (or sticky headers of list will cover item when scrolling)
        scrollIntoViewOnFocus && nextFocus.scrollIntoView(false);
        return true;
      }
    }
    return false;
  };
  switch (key) {
    case ARROW_UP:
      event.preventDefault();
      event.stopPropagation();
      if (isCascadingMenuItemOpen(elements[currentIndex])) {
        // focus on last item in menu
        shiftFocusToNextMenuLastItem(elements[currentIndex]);
      } else {
        shiftFocus(currentIndex, prevItem);
      }
      break;
    case ARROW_LEFT:
      event.preventDefault();
      if (isSubMenuItem(elements[currentIndex])) {
        event.stopPropagation();
        shiftFocusToPreviousMenu(elements[currentIndex]);
      } else {
        shiftFocus(currentIndex, prevItem);
      }
      break;
    case ARROW_DOWN:
      event.preventDefault();
      if (isCascadingMenuItemOpen(elements[currentIndex])) {
        // focus on 1st item in menu
        shiftFocusToNextMenu(elements[currentIndex]);
      } else {
        shiftFocus(currentIndex, nextItem);
      }
      break;
    case ARROW_RIGHT:
      event.preventDefault();
      shiftFocus(currentIndex, nextItem);
      break;
    case HOME:
      event.preventDefault();
      first && first.focus();
      break;
    case END:
      event.preventDefault();
      last && last.focus();
      break;
    default:
      break;
  }
};

// focus on list items, not list box
const handleFocus = (props, ref, isTabPressed, tabPressed = undefined, uniqueListIdentifier = undefined) => (event) => {
  if (!(ref && ref.current) || !props) {
    return;
  }
  const { onFocus, keyboardNavigation, keyboardFocus } = props;
  onFocus && onFocus(event);
  if (props.preventFocusHandling === true) {
    return;
  }
  if (!keyboardNavigation && !keyboardFocus) {
    return;
  }
  const wrapper = ref.current.querySelectorAll(`.${LIST_WRAPPER}`);
  const wrapperDiv = wrapper[0];
  if (isTabPressed) {
    tabPressed && tabPressed(false);
    return;
  }
  tabPressed && tabPressed(false);
  const current = document.activeElement;
  const focusedOnListWrapper = current.classList.contains(LIST_WRAPPER);
  if (focusedOnListWrapper) {
    wrapperDiv && wrapperDiv.blur();
    // get all list items
    const tabableClassName = uniqueListIdentifier ? `${LIST_ITEM_TABABLE_ITEM}-${uniqueListIdentifier}` : `${LIST_ITEM_TABABLE_ITEM}`;
    const elements = ref.current.querySelectorAll(`.${tabableClassName}`);
    // if any list item is selected, focus on the next one (if current one is not last in list)
    const selectedDivs = ref.current.querySelectorAll(`.${LIST_ITEM_SELECTED}`);
    const selectedDiv = selectedDivs && selectedDivs[0];
    if (selectedDiv) {
      selectedDiv.focus();
    } else {
      // focus on 1st list item
      const first = elements[0];
      first && first.focus();
    }
  }
};

export { handleKeyDown, handleFocus, CONSTANTS };